import React, { useState } from 'react';
import { sanitize as sanitizeHtml } from 'dompurify';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { useContactAdvisorQuery } from './api/useContactAdvisorQuery';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';
import { Separator } from '../CommonComponents/OPL/Separator/Separator';
import { AdditionalText, Avatar, CopyButton, StyleListItem } from './ContactAdvisor.styles';
import { toast } from '../CommonComponents/OPL/Toast/Toast';
import { Tooltip } from '../CommonComponents/OPL/Tooltip/Tooltip';
import { Link } from '../CommonComponents/OPL/Link/Link';
import { ContactAdvisorLoading } from './ContactAdvisorLoading';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import getAvatarPath from '../CommonUtils/getAvatarPath';

export const ContactAdvisor = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button variant="secondary" leftIcon="far fa-comments" onClick={() => setIsModalOpen(true)}>
        Contact my Advisor
      </Button>

      {isModalOpen && <ContactAdvisorModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

interface ModalProps {
  onClose(): void;
}

const ContactAdvisorModal = ({ onClose }: ModalProps) => {
  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      aria={{ labelledby: 'contact-advisor--modal-header' }}
      size="small"
    >
      <ModalHeader as="h2" id="contact-advisor--modal-header">
        Your Advisor
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody style={{ padding: 0 }}>
        <ContactAdvisorModalContent />
      </ModalBody>
    </Modal>
  );
};

const ContactAdvisorModalContent = () => {
  const { isLoading, isError, refetch, data } = useContactAdvisorQuery();

  if (isLoading) {
    return <ContactAdvisorLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    throw new Error('No contact advisor data');
  }

  const avatar = data.avatar ? data.avatar : data.avatarUrl && getAvatarPath(data.avatarUrl);

  return (
    <>
      <Box display="flex" alignItems="center" px="24" py="16" css="gap: 16px">
        {avatar && <Avatar url={avatar} />}

        <Text fontSize="h5" fontWeight={600} isOverflowWrap>
          {data.name}
        </Text>
      </Box>

      <Box as="ul" css="list-style: none" p="0">
        {data.address && (
          <ListItem icon="far fa-map-marker-alt" value={data.address}>
            <>
              {data.address.split('\n').map(addressLine => (
                <Text isOverflowWrap fontWeight={500}>
                  {addressLine}
                </Text>
              ))}
            </>
          </ListItem>
        )}
        {data.phone && (
          <ListItem icon="far fa-phone" value={data.phone}>
            <Text isOverflowWrap fontWeight={500}>
              {data.phone}
            </Text>
          </ListItem>
        )}
        {data.email && (
          <ListItem icon="far fa-envelope" value={data.email}>
            <Link isOverflowWrap href={`mailto:${data.email}`}>
              {data.email}
            </Link>
          </ListItem>
        )}
        {data.website && (
          <ListItem icon="far fa-link" value={data.website}>
            <Link isOverflowWrap href={data.website}>
              {data.websiteText || data.website}
            </Link>
          </ListItem>
        )}
      </Box>

      {data.additionalText && (
        <>
          <Separator />

          <AdditionalText dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.additionalText) }} />
        </>
      )}

      {data.additionalRepresentatives && data.additionalRepresentatives.length > 0 && (
        <>
          <Box
            px="24"
            py="8"
            bg="neutral.100"
            borderColor="neutral.200"
            borderStyle="solid"
            borderWidth="1px 0 0"
          >
            <Text variant="smallCaps">Support team</Text>
          </Box>

          {data.additionalRepresentatives.map(rep => (
            <>
              <Separator />

              <Box
                key={rep.name}
                display="flex"
                alignItems="center"
                px="24"
                py="16"
                css="gap: 12px"
              >
                <Box display="flex" flexDirection="column" flex={1}>
                  <Text fontWeight={600}>{rep.name}</Text>

                  {rep.role && (
                    <Text fontSize="small" fontWeight={500} color="neutral.500">
                      {rep.role}
                    </Text>
                  )}
                </Box>

                <Box display="flex" css="gap: 4px">
                  {rep.phone && (
                    <Tooltip content={rep.phone} placement="top">
                      <span>
                        <Link
                          variant="subtleInactive"
                          href={`tel:${rep.phone}`}
                          target="_self"
                          aria-label="Phone"
                        >
                          <Icon className="far fa-phone" aria-hidden />
                        </Link>
                      </span>
                    </Tooltip>
                  )}

                  {rep.email && (
                    <Tooltip content={rep.email} placement="top">
                      <span>
                        <Link
                          variant="subtleInactive"
                          href={`mailto:${rep.email}`}
                          target="_self"
                          aria-label="Email"
                        >
                          <Icon className="far fa-envelope" aria-hidden />
                        </Link>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </>
          ))}
        </>
      )}

      {data.calendarUrl && (
        <>
          <Separator />

          <Box display="flex" flexDirection="column" p="24">
            <Button
              leftIcon="fas fa-calendar-alt"
              onClick={() => {
                if (!data.calendarUrl) {
                  return;
                }

                window.open(data.calendarUrl, '_blank', 'noopener');
              }}
            >
              Schedule an appointment
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

interface ListItemProps {
  icon: string;
  value: string;
  children: React.ReactChild;
}

const ListItem = ({ icon, value, children }: ListItemProps) => {
  const [isCopiedToClipboard, setIsCopiedToClipboard] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value);

      if (!isCopiedToClipboard) {
        setIsCopiedToClipboard(true);
        setTimeout(() => setIsCopiedToClipboard(false), 1000);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to copy the URL to the clipboard, please copy it manually.');
    }
  };

  return (
    <>
      <Separator />

      <StyleListItem as="li">
        <Icon className={icon} color="neutral.500" />

        <Box flex={1}>{children}</Box>

        <Tooltip visible={isCopiedToClipboard} content="Copied!" placement="top">
          <CopyButton
            icon="far fa-clone"
            variant="subtleInactive"
            onClick={copyToClipboard}
            aria-label="Copy to clipboard"
          />
        </Tooltip>
      </StyleListItem>
    </>
  );
};
